import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Presentation, Sparkles, Loader2, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { DECK } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const STATUS_STYLE = {
  ready: "text-accent",
  generating: "text-amber-400",
  error: "text-red-400",
};

const IDEAS = [
  "Build an executive overview of our key metrics and recent trends.",
  "Create a quarterly performance deck with the most important breakdowns.",
  "Summarize what changed over time and where we should focus.",
];

export default function Presentations() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const [decks, srcs] = await Promise.all([
        api.get("/presentations", { params: { workspace_id: active.id } }),
        api.get("/sources", { params: { workspace_id: active.id } }),
      ]);
      setList(decks.data);
      setSources(srcs.data);
      setSourceId((prev) => (prev && srcs.data.find((s) => s.id === prev) ? prev : srcs.data[0]?.id || null));
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/presentations", {
        workspace_id: active.id,
        prompt: prompt.trim(),
        source_id: sourceId,
      });
      navigate(`/present/${data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not start the presentation.");
      setGenerating(false);
    }
  };

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    await api.delete(`/presentations/${id}`);
    load();
  };

  return (
    <div data-testid={DECK.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-5xl px-6 py-10">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Presentations</h1>
        <div aria-hidden="true" className="sema-accent-underline mt-4" />
        <p className="mt-5 max-w-xl text-sm text-muted-foreground">
          Describe what you want. An autonomous analyst designs the slides, runs the
          queries, writes the talking points, and hands you a deck — PPTX, PDF or HTML.
        </p>

        {/* Generator */}
        <div className="mt-8 rounded-2xl border border-border/60 bg-card/50 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-accent">
            <Sparkles className="h-4 w-4" /> Autonomous deck builder
          </div>
          <Textarea
            data-testid={DECK.promptInput}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. Build a Q1 performance overview highlighting admissions, occupancy and readmissions."
            rows={3}
            className="mt-4 resize-none text-sm"
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {IDEAS.map((idea) => (
                <button
                  key={idea}
                  onClick={() => setPrompt(idea)}
                  className="rounded-full border border-border/60 px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:border-accent/50 hover:text-foreground"
                >
                  {idea.slice(0, 42)}…
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {sources.length > 1 && (
                <Select value={sourceId || ""} onValueChange={setSourceId}>
                  <SelectTrigger data-testid={DECK.datasetPicker} className="h-9 w-auto min-w-[150px] text-xs">
                    <SelectValue placeholder="Dataset" />
                  </SelectTrigger>
                  <SelectContent className="font-mono">
                    {sources.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                data-testid={DECK.generateBtn}
                onClick={generate}
                disabled={generating || !prompt.trim() || !active}
                className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
              >
                {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Generate deck
              </Button>
            </div>
          </div>
        </div>

        {/* Past decks */}
        <div className="mt-10">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : list.length ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((d) => (
                <Link
                  key={d.id}
                  to={`/present/${d.id}`}
                  data-testid={DECK.card}
                  className="group relative rounded-xl border border-border/60 bg-card/50 p-5 transition-colors hover:border-accent/50"
                >
                  <Presentation className="h-5 w-5 text-accent" />
                  <h3 className="mt-4 flex items-start gap-1 text-sm font-semibold">
                    <span className="line-clamp-2">{d.status === "generating" ? d.prompt : d.title}</span>
                    <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground">
                    <span className={STATUS_STYLE[d.status] || ""}>{d.status}</span> · {d.slide_count} slide(s)
                    {d.share_mode === "public" ? " · Public" : ""}
                  </p>
                  <button
                    onClick={(e) => remove(d.id, e)}
                    aria-label="Delete presentation"
                    className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </Link>
              ))}
            </div>
          ) : (
            <div data-testid={DECK.empty} className="rounded-xl border border-dashed border-border/60 p-12 text-center">
              <Presentation className="mx-auto h-8 w-8 text-accent" />
              <p className="mt-4 text-sm text-foreground/90">No presentations yet.</p>
              <p className="mt-1 text-xs text-muted-foreground">Describe a deck above and let the analyst build it.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
